
// Vue
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { namespace, Getter, Mutation, Action, State } from "vuex-class";

// components
import SureViewIcon from '../SureViewIcon.vue';

// types
import { AudioDevice, AudioOptions, AudioTypes } from "@/types/audio/audio";

// constants
import MapLayerItemTypes from "@/constants/MapLayerItemTypes";

// services
import api from '@/services/api.service';
import { ActiveMapItem } from '@/store/site-monitor/types';
import { MapItemTypeData } from '@/store/map-layers/types';
import Receive from './Receive.vue';

// namespaces
const DeviceStore = namespace("devices");
const SiteMonitor = namespace("siteMonitor");
const MapLayers = namespace("mapLayers");

@Component({
    components: {
        "sureview-icon": SureViewIcon
    }
})
export default class AudioList extends Vue {
    @Prop({ default: () => [], type: Array }) audioDeviceIds: number[];

    private audioReceive: AudioDevice | null= null;
    private audioTransmit: AudioDevice | null  = null;
    private twoWayAudio: AudioDevice | null = null;
    private open: boolean = true;

    private audioDevices: AudioDevice[] = [];

    private audioOptionTypes: any = AudioTypes;

    private busy: boolean = true;

    // -- LifeCycle Hooks -- //

    private async mounted() {
        this.busy = false;
       await this.fetchAudio();
    }

    // -- Computed properties -- //
    
    /**
     * @summary computed property which will filter audio on if they can; transmit, receive or twoWay.
     * @returns { AudioDevice[] } array of audioDevices
     */
    private get usableAudio(): AudioDevice[] {
        return this.audioDevices ? this.audioDevices.filter(x => x.canReceive || x.canTransmit || x.canTwoway) : [];
    }

    // -- Watchers -- //

    @Watch("audioDeviceIds", { deep: true })
    private nearbyDeviceChange() {
        // Check if there is any new devices
        this.fetchAudio();
    }

    // -- Methods -- //

    /**
     * @summary method which will fetch the desired audio devices by id's
     */
    private async fetchAudio(): Promise<void> {        
        // NOTE a good improvement for this will be keeping the audio devices in the store so,
        // if the operator moves around the map a lot back and fore. We shouldn't need to fetch
        // all the same devices or some of the same devices etc. Future improvement!

        // reset audio list
        this.audioDevices = [];

        if (!this.audioDeviceIds.length) return;

        const { data }: { data: AudioDevice[] } = await api.fetchAudioDevicesByIds(this.audioDeviceIds);

        if (!data) return;

        this.audioDevices = data;
    }

    /**
     * @summary method which will set the desired audio type.
     * this method will unset the other audio types for the one currently being set.
     */
    private toggleAudioOption(audioOptionType: AudioTypes, audio: AudioDevice): void {
        switch (audioOptionType) {
            case AudioTypes.receive:
                this.setDefault({ twowayAudio: false, receive: !!this.audioReceive });
                this.emitReceive(this.audioReceive && audio.deviceID == this.audioReceive.deviceID ? null : audio);    
                break;
            case AudioTypes.transmit:  
                this.setDefault({ twowayAudio: false, transmit: !!this.audioTransmit });
                this.emitTransmit(this.audioTransmit && audio.deviceID == this.audioTransmit.deviceID ? null : audio)    
                break;
            case AudioTypes.twowayAudio:
                this.setDefault({ transmit: false, receive: false });
                this.emitTwowayAudio(this.twoWayAudio && audio.deviceID == this.twoWayAudio.deviceID ? null : audio);           
                break;
            default:
                this.setDefault({ transmit: false, twowayAudio: false, receive: false });
                break;
        }
    }

    /**
     * @summary method which will emit null to all audio types except the one being set.
     */
    private setDefault({ receive = true, transmit = true, twowayAudio = true } : { receive?: boolean, transmit?: boolean, twowayAudio?: boolean }) {
        if (!receive)  this.emitReceive(null);
        if (!transmit) this.emitTransmit(null);
        if (!twowayAudio) this.emitTwowayAudio(null);
    }


    /**
     * @summary method to emit transmit audio
     */
    private emitTransmit(audioDevice: AudioDevice | null) {
        this.audioTransmit = audioDevice;
        this.$emit("set-transmit", audioDevice);
    }

    /**
     * @summary method to emit receive audio
     */
    private emitReceive(audioDevice: AudioDevice | null) {
        this.audioReceive = audioDevice;
        this.$emit("set-receive", audioDevice);
    }

    /**
     * @summary method to emit twowway audio
     */
    private emitTwowayAudio(audioDevice: AudioDevice | null) {
        this.twoWayAudio = audioDevice;
        this.$emit("set-twoway-audio", audioDevice);
    }
}
